import {
  CellValueChangedEvent,
  ColDef,
  GridOptions,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { Component, ElementRef, OnInit } from '@angular/core'
import {
  EditableCheckboxRenderer,
  heliosDefaultColDef,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import { ModalLinkComponent } from '@northfork/ag-grid-angular'
import { BulkDirectTransferRow } from '../../model/bulk-transaction/bulk-direct-transfer/bulk-direct-transfer.model'
import { BulkSecurityTransferRow } from '../../model/bulk-transaction/bulk-security-transfer/bulk-security-transfer.model'
import { MvtBasisSelection } from '../../model/mvt-basis.model'
import {
  deduceValidityStatusCellClass,
  setHighestZIndexForOpenModals,
} from '../../util/common-utils'
import { TransactionConstants } from '../../util/constants'

@Component({
  selector: 'hs-bv-mv-basis',
  templateUrl: './bv-mv-basis-selector.component.html',
  styleUrls: ['./bv-mv-basis-selector.component.scss'],
})
export class BvMvBasisComponent
  implements
    ModalLinkComponent<BulkDirectTransferRow | BulkSecurityTransferRow>,
    OnInit
{
  // The data and params will automatically be populated by the ModalLinkRenderer
  // Data is the data of the row
  data?: BulkDirectTransferRow | BulkSecurityTransferRow
  // Params are the params of the row clicked
  params?: ICellRendererParams

  loadingBasis = false
  mvtBasisSelectionRows: MvtBasisSelection[] = []
  gridApi?: AgGridApi
  colDefs: ColDef[] = this.getColDefs()

  readonly compId = TransactionConstants.BV_MV_BASIS_COMP_ID

  gridOptions: GridOptions = this.createGridOptions()
  readonly title = 'Book Value / Market Value Selections'
  readonly generalWarning =
    '* Adjusting the default selection is not recommended unless requested by client. Doing so can cause downstream lot link errors.'
  readonly basisMultiAccountWarning =
    'Basis is shared by both accounts.MV is recommended.'
  constructor(
    readonly activeModal: NgbActiveModal,
    private readonly eleRef: ElementRef,
  ) {}

  ngOnInit() {
    if (!this.data) {
      return
    }
    setHighestZIndexForOpenModals(this.compId, this.eleRef)
    // to create a copy so that cancel operation doent polllur the dialogues mvtBasisSelection value
    this.mvtBasisSelectionRows = this.data.mvtBasisSelection
      ? this.data.mvtBasisSelection.map((basisSelected: MvtBasisSelection) => {
          return {
            basis: basisSelected.basis,
            accounts: basisSelected.accounts,
            bookValue: basisSelected.bookValue,
            marketValue: basisSelected.marketValue,
          }
        })
      : []
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: 'warningCell',
        headerName: '',
        valueGetter: () => '',
        cellClass: params =>
          deduceValidityStatusCellClass(
            params.data.accounts.length > 1 ? 'warning' : '',
          ),
        tooltipComponentParams: {
          showTooltip: params => params?.data.accounts.length > 1,
        } as NfTooltipParams,
        width: 30,
        tooltipValueGetter: params =>
          params.data.accounts.length > 1 ? this.basisMultiAccountWarning : '',
      },
      {
        field: 'basis',
        valueGetter: params => params.data.basis.prefix,
        width: 95,
      },
      {
        field: 'accounts',
        valueGetter: params => params.data.accounts.join(','),
        headerName: 'Account(s)',
        width: 125,
      },
      {
        field: 'bookValue',
        cellRenderer: EditableCheckboxRenderer,
        width: 95,
      },

      {
        field: 'marketValue',
        cellRenderer: EditableCheckboxRenderer,
        width: 105,
      },
    ]
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: event => {
        this.gridApi = event
        event.api.setDomLayout('autoHeight')
      },
      getRowNodeId: ({ basis }: MvtBasisSelection) => basis.prefix,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: 40,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      columnDefs: this.colDefs,
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    /* Category onChange event handling */
    const colId = event.column.getColId()

    switch (colId) {
      case 'marketValue':
        if (event.newValue != event.oldValue)
          event.node.setDataValue('bookValue', !event.node.data.marketValue)
        break
      case 'bookValue':
        if (event.newValue != event.oldValue)
          event.node.setDataValue('marketValue', !event.node.data.bookValue)
        break
    }
  }

  save() {
    this.activeModal.close(this.mvtBasisSelectionRows)
  }

  getComponentId() {
    return this.compId
  }
}
